/**
 * The browser's `ai_turn` event (TEL, 2026-10-19; spec 2026-10-18 AI companion
 * growth architecture, section 10): one per companion turn, from the
 * CompanionManager's turn record (CompanionConfig.onTurnTelemetry, wired in
 * app-layout). It joins the server's companion_turn_routed, $ai_generation and
 * analyst_turn events by `client_turn_id`, the id every request of the turn
 * sends. Ids, counts, times and tool names only: no message text.
 */
import { toWireMode, type AiTurnTelemetry } from '@edge/edge-ai'
import { EVENTS, trackEvent } from '@/services/analytics'
import type { AiTurnPayload } from '@/services/analytics/events'

export function aiTurnPayload(turn: AiTurnTelemetry): AiTurnPayload {
  return {
    client_turn_id: turn.clientTurnId,
    path: turn.path,
    modality: turn.modality,
    // Analytics keeps the Ask/Do toggle's names ('ask', 'do'), so events from
    // before and after the rename compare.
    mode: toWireMode(turn.access),
    outcome: turn.outcome,
    ttfw_ms: turn.ttfwMs,
    ack_ms: turn.ackMs,
    total_ms: turn.totalMs,
    rounds: turn.rounds,
    tools_called: turn.toolsCalled,
    tool_calls: Object.values(turn.toolsCalled).reduce((n, c) => n + c, 0),
    get_tools_lookups: turn.getToolsLookups,
    not_offered: turn.notOffered,
    not_offered_calls: turn.notOffered.length,
    prompt_tokens: turn.promptTokens,
    cached_tokens: turn.cachedTokens,
    tool_delivery: turn.toolDelivery,
  }
}

/** CompanionConfig.onTurnTelemetry; the manager keeps a failure here out of the turn. */
export function trackAiTurn(turn: AiTurnTelemetry): void {
  trackEvent(EVENTS.AI_TURN, aiTurnPayload(turn))
}
